/**
 * Header Component
 * Requirements: 3.3
 */
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { Resume, UserPreferences } from '../types';
import {
  Menu,
  X,
  Plus,
  User,
  LogOut,
  Settings,
  FileText,
  Link,
  ChevronDown,
  Edit,
  Home,
  Info,
  Mail,
  Sparkles,
} from 'lucide-react';
import version from '../../../version.txt?raw';

interface HeaderProps {
  onAddJobUrl: () => void;
  onAddJobManual: () => void;
  onAddJobEmail: () => void;
  onOpenSettings: () => void;
  selectedResume: string | null;
  onSelectResume: (resumename: string) => void;
  onEditResume: (resumename: string) => void;
  onNewResume: () => void;
  userPreferences?: UserPreferences;
  onPreferencesChange?: (prefs: Partial<UserPreferences>) => void;
  resumeRefreshKey?: number;
}

export function Header({
  onAddJobUrl,
  onAddJobManual,
  onAddJobEmail,
  onOpenSettings,
  selectedResume,
  onSelectResume,
  onEditResume,
  onNewResume,
  userPreferences,
  onPreferencesChange: _onPreferencesChange,
  resumeRefreshKey,
}: HeaderProps) {
  const { user, logout } = useAuth();
  const [showResumeDropdown, setShowResumeDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(true);

  const resumeDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadResumes();
  }, [resumeRefreshKey]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (resumeDropdownRef.current && !resumeDropdownRef.current.contains(event.target as Node)) {
        setShowResumeDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function loadResumes() {
    try {
      const response = await api.listResumes();
      setResumes(response.resumes);
      if (response.resumes.length > 0 && !selectedResume) {
        onSelectResume(response.resumes[0].resumename);
      }
    } catch (error) {
      console.error('Failed to load resumes:', error);
    } finally {
      setLoadingResumes(false);
    }
  }

  function handleReturnToLanding() {
    window.open('https://skillsnap.me', '_blank');
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - fully left justified */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-2">
              <img src="/assets/skillsnap_logo.png" alt="SkillSnap" className="h-8 w-auto" />
              <span className="text-xl font-semibold text-primary-600">SkillSnap</span>
            </a>
          </div>

          {/* Desktop Navigation - fully right justified */}
          <nav className="hidden md:flex items-center space-x-4 ml-auto">
            {/* Upgrade Now - only for trial users */}
            {userPreferences?.status === 'trial' && (
              <a
                href="https://skillsnap.me/pricing.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Upgrade Now</span>
              </a>
            )}
            {/* Resume Selector */}
            <div className="relative" ref={resumeDropdownRef}>
              <button
                onClick={() => setShowResumeDropdown(!showResumeDropdown)}
                className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              >
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700 max-w-32 truncate">
                  {loadingResumes ? 'Loading...' : (selectedResume || 'Select Resume')}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              
              {showResumeDropdown && (
                <div role="menu" className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  {resumes.map((resume) => (
                    <div
                      key={resume.resumename}
                      className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-100 ${
                        selectedResume === resume.resumename ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                      }`}
                    >
                      <button
                        onClick={() => {
                          onSelectResume(resume.resumename);
                          setShowResumeDropdown(false);
                        }}
                        className="flex-1 text-left truncate"
                      >
                        {resume.resumename}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // If not selected, select it first then edit
                          if (selectedResume !== resume.resumename) {
                            onSelectResume(resume.resumename);
                          }
                          onEditResume(resume.resumename);
                          setShowResumeDropdown(false);
                        }}
                        className="p-1 hover:bg-gray-200 rounded ml-2"
                        title="Edit resume"
                        aria-label="Edit resume"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {resumes.length === 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No resumes yet
                    </div>
                  )}
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      onNewResume();
                      setShowResumeDropdown(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-primary-600 hover:bg-gray-100"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Resume
                  </button>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm text-gray-700">{user?.username || 'User'}</span>
              </button>

              {showUserMenu && (
                <div role="menu" className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <button
                    role="menuitem"
                    onClick={() => { onOpenSettings(); setShowUserMenu(false); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { handleReturnToLanding(); setShowUserMenu(false); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Website
                  </button>
                  <div className="flex items-center px-4 py-2 text-sm text-gray-500">
                    <Info className="w-4 h-4 mr-2" />
                    Version {version.trim()}
                  </div>
                  <hr className="my-1" />
                  <button
                    role="menuitem"
                    onClick={() => { logout(); setShowUserMenu(false); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-2">
            {userPreferences?.status === 'trial' && (
              <a
                href="https://skillsnap.me/pricing.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center w-full px-4 py-2 text-sm font-semibold text-amber-600 hover:bg-amber-50 rounded-lg"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade Now
              </a>
            )}
            <button
              onClick={() => { onAddJobUrl(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Link className="w-4 h-4 mr-2" />
              Add Job from URL
            </button>
            <button
              onClick={() => { onAddJobManual(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <FileText className="w-4 h-4 mr-2" />
              Add Job Manually
            </button>
            <button
              onClick={() => { onAddJobEmail(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Mail className="w-4 h-4 mr-2" />
              Add Job from Email
            </button>
            <hr />
            <button
              onClick={() => { onOpenSettings(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
            <button
              onClick={() => { handleReturnToLanding(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Home className="w-4 h-4 mr-2" />
              Website
            </button>
            <div className="flex items-center px-4 py-2 text-sm text-gray-500">
              <Info className="w-4 h-4 mr-2" />
              Version {version.trim()}
            </div>
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 rounded-lg"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      )}

    </header>
  );
}
